import ThrottledApi from './api/throttledApi';
import { AxiosResponse } from 'axios';
import {
  ReconciliationData,
  ReconciliationFilters,
  ReconciliationResponse,
  StoreReconciliationFilters
} from '../types';
import { RECONCILIATION_ENDPOINTS } from '../constants/apiEndPoints';

/**
 * ReconciliationService
 * 
 * This service handles all audit reconciliation operations using ThrottledApi
 * for automatic request queuing and rate limiting.
 * 
 * Key Features:
 * 1. Uses ThrottledApi for automatic request queuing (no dropped requests)
 * 2. Separate queues for different operation types (read/write/search)
 * 3. Comprehensive error handling and logging
 * 4. TypeScript support with proper interfaces
 * 
 * Queue Behavior:
 * - GET requests: max 3 concurrent (readQueue)
 * - POST/PUT/DELETE requests: max 2 concurrent (writeQueue)
 * - Search requests: max 1 concurrent (searchQueue)
 * - All requests are queued and processed (no dropping)
 */

// ===== STORE-SPECIFIC OPERATIONS =====

const getErrorMessage = (error: any): string => {
  const detail = error.response?.data?.detail;
  if (typeof detail === 'string') {
    return detail;
  }
  if (typeof error.response?.data === 'string') {
    return error.response.data;
  }
  return error.message || 'Request failed';
};

/**
 * Create a new audit reconciliation for a store
 * 
 * @param storeId - The ID of the store
 * @param reconciliationData - The reconciliation data to create
 * @returns Promise<ReconciliationResponse>
 */
export const createReconciliation = async (
  storeId: string,
  reconciliationData: Partial<ReconciliationData>
): Promise<ReconciliationResponse> => {
  console.log('🔍 ReconciliationService: Creating reconciliation for store:', storeId, 'data:', reconciliationData);

  try {
    const response = await ThrottledApi.post(
      RECONCILIATION_ENDPOINTS.CREATE_STORE_RECONCILIATION(storeId),
      reconciliationData
    ) as AxiosResponse;

    console.log('🔍 ReconciliationService: Reconciliation created successfully');
    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.log('🔍 ReconciliationService: Reconciliation creation failed:', error);
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message
    };
  }
};

/**
 * Upsert acknowledge and verified details for a store
 * 
 * @param storeId - The ID of the store
 * @param data - The acknowledge and verified data to upsert
 * @returns Promise<ReconciliationResponse>
 */
export const upsertAcknowledgeAndVerified = async (
  storeId: string,
  data: {
    reconciliation_date: string;
    is_verified: boolean;
    is_ack: boolean;
    notes: string;
  }
): Promise<ReconciliationResponse> => {
  console.log('🔍 ReconciliationService: Upserting acknowledge and verified for store:', storeId, 'data:', data);

  try {
    const response = await ThrottledApi.post(
      RECONCILIATION_ENDPOINTS.UPSERT_RECONCILIATION(storeId),
      data
    ) as AxiosResponse;

    console.log('🔍 ReconciliationService: Acknowledge and verified upserted successfully');
    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.log('🔍 ReconciliationService: Acknowledge and verified upsert failed:', error);
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message
    };
  }
};

export const addManagerReconciliationNote = async (
  storeId: string,
  data: {
    reconciliation_date: string;
    note: string;
  }
): Promise<ReconciliationResponse> => {
  try {
    const response = await ThrottledApi.post(
      RECONCILIATION_ENDPOINTS.ADD_MANAGER_NOTE(storeId),
      data
    ) as AxiosResponse;

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    return {
      success: false,
      data: null,
      error: getErrorMessage(error)
    };
  }
};

export const verifyDailyReconciliation = async (
  storeId: string,
  data: {
    reconciliation_date: string;
    verification_note: string;
  }
): Promise<ReconciliationResponse> => {
  try {
    const response = await ThrottledApi.post(
      RECONCILIATION_ENDPOINTS.VERIFY_DAILY_RECONCILIATION(storeId),
      data
    ) as AxiosResponse;

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    return {
      success: false,
      data: null,
      error: getErrorMessage(error)
    };
  }
};

export const addReconciliationVerificationNote = async (
  storeId: string,
  data: {
    reconciliation_date: string;
    verification_note: string;
  }
): Promise<ReconciliationResponse> => {
  try {
    const response = await ThrottledApi.post(
      RECONCILIATION_ENDPOINTS.ADD_VERIFICATION_NOTE(storeId),
      data
    ) as AxiosResponse;

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    return {
      success: false,
      data: null,
      error: getErrorMessage(error)
    };
  }
};

/**
 * Get audit reconciliations for a specific store
 * 
 * @param storeId - The ID of the store
 * @param filters - Optional filters for the query
 * @returns Promise<ReconciliationResponse>
 */
export const getStoreReconciliations = async (
  storeId: string,
  filters: StoreReconciliationFilters = {}
): Promise<ReconciliationResponse> => {
  const { dateFrom, dateTo, page = 1, size = 20, status } = filters;
  let url = `${RECONCILIATION_ENDPOINTS.LIST_STORE_RECONCILIATIONS(storeId)}?page=${page}&size=${size}`;

  if (dateFrom) url += `&date_from=${dateFrom}`;
  if (dateTo) url += `&date_to=${dateTo}`;
  if (status) url += `&status=${status}`;

  console.log('🔍 ReconciliationService: Getting store reconciliations for store:', storeId, 'filters:', filters);

  try {
    const response = await ThrottledApi.get(url) as AxiosResponse;

    console.log('🔍 ReconciliationService: Store reconciliations retrieved successfully');
    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.log('🔍 ReconciliationService: Store reconciliations retrieval failed:', error);
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message
    };
  }
};

/**
 * Get acknowledge and verified details for a store
 * 
 * @param storeId - The ID of the store
 * @param date - The date
 * @returns Promise<ReconciliationResponse>
 */
export const getAcknowledgeAndVerifiedDetails = async (
  storeId: string,
  date: string
): Promise<ReconciliationResponse> => {
  console.log('🔍 ReconciliationService: Getting acknowledge and verified details for store:', storeId, 'date:', date);

  try {
    const response = await ThrottledApi.get(
      RECONCILIATION_ENDPOINTS.STORE_RECONCILIATION_WITH_DATA(storeId, date)
    ) as AxiosResponse;

    console.log('🔍 ReconciliationService: Acknowledge and verified details retrieved successfully');
    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.log('🔍 ReconciliationService: Acknowledge and verified details retrieval failed:', error);
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message
    };
  }
};

// ===== GENERAL RECONCILIATION OPERATIONS =====

/**
 * Get a specific audit reconciliation by ID
 * 
 * @param reconciliationId - The ID of the reconciliation
 * @returns Promise<ReconciliationResponse>
 */
export const getReconciliation = async (reconciliationId: string): Promise<ReconciliationResponse> => {
  console.log('🔍 ReconciliationService: Getting reconciliation by ID:', reconciliationId);

  try {
    const response = await ThrottledApi.get(
      RECONCILIATION_ENDPOINTS.GET_RECONCILIATION(reconciliationId)
    ) as AxiosResponse;

    console.log('🔍 ReconciliationService: Reconciliation retrieved successfully');
    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.log('🔍 ReconciliationService: Reconciliation retrieval failed:', error);
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message
    };
  }
};

/**
 * Update an existing audit reconciliation
 * 
 * @param reconciliationId - The ID of the reconciliation to update
 * @param updateData - The data to update
 * @returns Promise<ReconciliationResponse>
 */
export const updateReconciliation = async (
  reconciliationId: string,
  updateData: Partial<ReconciliationData>
): Promise<ReconciliationResponse> => {
  console.log('🔍 ReconciliationService: Updating reconciliation:', reconciliationId, 'data:', updateData);

  try {
    const response = await ThrottledApi.put(
      RECONCILIATION_ENDPOINTS.UPDATE_RECONCILIATION(reconciliationId),
      updateData
    ) as AxiosResponse;

    console.log('🔍 ReconciliationService: Reconciliation updated successfully');
    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.log('🔍 ReconciliationService: Reconciliation update failed:', error);
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message
    };
  }
};

/**
 * Get all audit reconciliations with optional filters
 * 
 * @param filters - Optional filters for the query
 * @returns Promise<ReconciliationResponse>
 */
export const getReconciliations = async (
  filters: ReconciliationFilters = {}
): Promise<ReconciliationResponse> => {
  const { storeId, userId, dateFrom, dateTo, page = 1, size = 20, status } = filters;
  let url = `${RECONCILIATION_ENDPOINTS.LIST_RECONCILIATIONS()}?page=${page}&size=${size}`;

  if (storeId) url += `&store_id=${storeId}`;
  if (userId) url += `&user_id=${userId}`;
  if (dateFrom) url += `&date_from=${dateFrom}`;
  if (dateTo) url += `&date_to=${dateTo}`;
  if (status) url += `&status=${status}`;

  console.log('🔍 ReconciliationService: Getting reconciliations with filters:', filters);

  try {
    const response = await ThrottledApi.get(url) as AxiosResponse;

    console.log('🔍 ReconciliationService: Reconciliations retrieved successfully');
    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.log('🔍 ReconciliationService: Reconciliations retrieval failed:', error);
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message
    };
  }
};

/**
 * Delete a reconciliation
 * 
 * @param reconciliationId - The ID of the reconciliation to delete
 * @returns Promise<ReconciliationResponse>
 */
export const deleteReconciliation = async (reconciliationId: string): Promise<ReconciliationResponse> => {
  console.log('🔍 ReconciliationService: Deleting reconciliation:', reconciliationId);

  try {
    const response = await ThrottledApi.delete(
      RECONCILIATION_ENDPOINTS.DELETE_RECONCILIATION(reconciliationId)
    ) as AxiosResponse;

    console.log('🔍 ReconciliationService: Reconciliation deleted successfully');
    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.log('🔍 ReconciliationService: Reconciliation deletion failed:', error);
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message
    };
  }
};

/**
 * Resolve discrepancies in a reconciliation
 * 
 * @param reconciliationId - The ID of the reconciliation
 * @param discrepancyResolutions - Array of discrepancy resolutions
 * @returns Promise<ReconciliationResponse>
 */
export const resolveDiscrepancies = async (
  reconciliationId: string,
  discrepancyResolutions: Array<{
    discrepancy_id: string;
    resolved: boolean;
    resolution_notes?: string;
  }>
): Promise<ReconciliationResponse> => {
  console.log('🔍 ReconciliationService: Resolving discrepancies for reconciliation:', reconciliationId);

  try {
    const response = await ThrottledApi.patch(
      RECONCILIATION_ENDPOINTS.RESOLVE_DISCREPANCIES(reconciliationId),
      { discrepancy_resolutions: discrepancyResolutions }
    ) as AxiosResponse;

    console.log('🔍 ReconciliationService: Discrepancies resolved successfully');
    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.log('🔍 ReconciliationService: Discrepancy resolution failed:', error);
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message
    };
  }
};

/**
 * Get queue status for monitoring
 * 
 * @returns Object with queue information
 */
export const getQueueStatus = () => {
  return ThrottledApi.getQueueStatus();
};

/**
 * Clear all queues (use with caution)
 */
export const clearQueues = () => {
  ThrottledApi.clearQueues();
};

/**
 * Pause all queues
 */
export const pauseQueues = () => {
  ThrottledApi.pauseQueues();
};

/**
 * Resume all queues
 */
export const resumeQueues = () => {
  ThrottledApi.resumeQueues();
};
